import React from 'react';
import { Check, Sparkles, Tag } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Pricing = () => {
  const { t } = useLanguage();

  const scrollToContact = (e) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing" className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-cyan-600/5 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            {t.pricing.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-4xl mx-auto">
            {t.pricing.subtitle}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
          {t.pricing.plans.map((plan, index) => (
            <div
              key={index}
              className={`relative group ${
                plan.popular ? 'lg:scale-105 z-10' : ''
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                  <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full text-white text-sm font-bold">
                    <Sparkles className="w-4 h-4" />
                    <span>POPULAR</span>
                  </div>
                </div>
              )}

              {/* Glow Effect */}
              <div
                className={`absolute -inset-1 bg-gradient-to-r ${
                  plan.popular
                    ? 'from-cyan-600 to-blue-600 opacity-40'
                    : 'from-cyan-600 to-blue-600 opacity-20'
                } rounded-lg blur group-hover:opacity-40 transition duration-300`}
              />

              {/* Card */}
              <div
                className={`relative bg-gradient-to-br from-cyan-950/50 to-blue-950/30 border ${
                  plan.popular ? 'border-cyan-500/50' : 'border-cyan-500/30'
                } rounded-lg p-8 h-full flex flex-col`}
              >
                {/* Plan Name */}
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>

                {/* Price */}
                <div className="mb-6">
                  <span className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-gray-400 ml-2">/ {plan.period}</span>
                  )}
                  {plan.price !== 'CHF 0' && (
                    <p className="text-gray-500 text-xs mt-1">{t.pricing.vatNote}</p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <a
                  href="https://app.scanfire.ch/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full py-3 rounded-lg font-semibold text-center transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/50'
                      : 'bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-600/30'
                  }`}
                >
                  {t.pricing.ctaRegister}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Add-on Modules (Zusatzmodule) */}
        <div className="mt-20">
          {/* Divider with title */}
          <div className="flex items-center gap-6 mb-10">
            <div className="flex-grow h-px bg-gradient-to-r from-transparent to-cyan-500/40" />
            <span className="text-lg md:text-xl font-bold tracking-widest uppercase bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {t.pricing.addonsTitle}
            </span>
            <div className="flex-grow h-px bg-gradient-to-l from-transparent to-cyan-500/40" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[2fr_2fr_1fr] gap-8 items-stretch">
            {/* Add-on Cards */}
            {t.pricing.addons.map((addon, index) => (
              <div key={index} className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-20 rounded-lg blur group-hover:opacity-40 transition duration-300" />
                <div className="relative bg-gradient-to-br from-cyan-950/50 to-blue-950/30 border border-cyan-500/30 rounded-lg p-8 h-full flex flex-col">
                  {/* Name */}
                  <h3 className="text-2xl font-bold text-white mb-2">{addon.name}</h3>

                  {/* Price */}
                  <div className="mb-2">
                    <span className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                      {addon.price}
                    </span>
                    {addon.period && (
                      <span className="text-gray-400 ml-2">/ {addon.period}</span>
                    )}
                    <p className="text-gray-500 text-xs mt-1">{t.pricing.vatNote}</p>
                  </div>

                  {/* Note */}
                  {addon.note && (
                    <p className="text-gray-400 text-sm mb-6">{addon.note}</p>
                  )}

                  {/* Features (two columns) */}
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8 flex-grow">
                    {addon.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <a
                    href="#contact"
                    onClick={scrollToContact}
                    className="w-full py-3 rounded-lg font-semibold text-center transition-all duration-300 bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-600/30"
                  >
                    {t.pricing.ctaLearnMore}
                  </a>
                </div>
              </div>
            ))}

            {/* Annual Discount Box */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-30 rounded-lg blur group-hover:opacity-50 transition duration-300" />
              <div className="relative bg-gradient-to-br from-cyan-950/50 to-blue-950/30 border border-cyan-500/40 rounded-lg p-8 h-full flex flex-col items-center text-center">
                <Tag className="w-10 h-10 text-cyan-400 mb-4" />
                <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-4">
                  {t.pricing.annualDiscount.title}
                </h3>
                <span className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  {t.pricing.annualDiscount.value}
                </span>
                <p className="text-gray-400 text-sm mt-2 mb-8 flex-grow">
                  {t.pricing.annualDiscount.note}
                </p>
                <a
                  href="#contact"
                  onClick={scrollToContact}
                  className="w-full py-3 rounded-lg font-semibold text-center transition-all duration-300 bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-600/30"
                >
                  {t.pricing.ctaLearnMore}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
